import { z } from 'zod'

import { OperationStateSchema } from '@/lib/api/schemas/operations.schema'

const Timestamp = z.iso.datetime({ offset: true })

export const OnecSyncKindSchema = z.enum(['costs', 'pieces_full'])
export const OnecSyncRequestStateSchema = z.enum(['pending', 'claimed', 'done', 'failed', 'expired'])
export const OnecSyncerHealthSchema = z.enum(['alive', 'silent', 'never_seen'])
export const OnecSyncRunOutcomeSchema = z.enum(['completed', 'unchanged', 'failed'])
export const OnecSyncFailureReasonSchema = z.enum([
  'onec_com_unavailable',
  'onec_base_unreachable',
  'onec_extract_failed',
  'onec_extract_invalid',
  'syncer_internal_error',
])

export const OnecSyncRequestSchema = z.object({
  id: z.string().uuid(),
  kinds: z.array(OnecSyncKindSchema).min(1),
  state: OnecSyncRequestStateSchema,
  requestedBy: z.string().min(1),
  requestedAt: Timestamp,
  claimedAt: Timestamp.nullable(),
  settledAt: Timestamp.nullable(),
  runId: z.string().uuid().nullable(),
}).strict()

export const OnecSyncRunSchema = z.object({
  id: z.string().uuid(),
  receivedAt: Timestamp,
  startedAt: Timestamp,
  finishedAt: Timestamp,
  trigger: z.enum(['schedule', 'request']),
  requestId: z.string().uuid().nullable(),
  outcome: OnecSyncRunOutcomeSchema,
  failureReason: OnecSyncFailureReasonSchema.nullable(),
  syncerVersion: z.string().min(1),
  costRows: z.number().int().nonnegative().nullable(),
  operationId: z.string().uuid().nullable(),
}).strict()

export const OnecSyncerStatusSchema = z.object({
  serverAt: Timestamp,
  health: OnecSyncerHealthSchema,
  paused: z.boolean(),
  heartbeat: z.object({
    lastHeartbeatAt: Timestamp,
    syncerSentAt: Timestamp,
    syncerVersion: z.string().min(1),
    contractVersion: z.string().min(1),
  }).strict().nullable(),
  openRequest: OnecSyncRequestSchema.nullable(),
  recentRuns: z.array(OnecSyncRunSchema),
  lastCostRun: OnecSyncRunSchema.nullable(),
  costCoverage: z.object({
    pieces: z.number().int().nonnegative(),
    withCost: z.number().int().nonnegative(),
    // Reason code -> pieces without a 1C cost; read leniently so a new reason still shows.
    missing: z.record(z.string(), z.number().int().nonnegative()),
  }).strict(),
}).strict()

export const OnecSyncRequestCreatedSchema = z.object({
  request: OnecSyncRequestSchema,
  created: z.boolean(),
}).strict()

export const OnecSyncSettingsSchema = z.object({
  paused: z.boolean(),
  pausedBy: z.string().nullable(),
  pausedAt: Timestamp.nullable(),
  autoApply: z.boolean(),
  autoApplyBy: z.string().nullable(),
  autoApplyAt: Timestamp.nullable(),
  autoApplySales: z.boolean(),
  autoApplySalesBy: z.string().nullable(),
  autoApplySalesAt: Timestamp.nullable(),
}).strict()

export const OnecPieceDiffClassSchema = z.enum(['create', 'update', 'unchanged', 'unmatched'])

export const OnecPieceDiffLineSchema = z.object({
  uin: z.string().min(1),
  productId: z.string().uuid().nullable(),
  class: OnecPieceDiffClassSchema,
  changes: z.record(z.string(), z.object({ from: z.string().nullable(), to: z.string().nullable() }).strict()),
  stateFrom: z.string().nullable(),
  stateTo: z.enum(['in_stock', 'sold']).nullable(),
  stateReason: z.string().nullable(),
  notes: z.array(z.string()),
  applied: z.boolean(),
}).strict()

export const OnecPieceDiffFilterSchema = z.enum(['all', 'sold', 'in_stock', 'price', 'review'])

export const OnecPieceDiffSchema = z.object({
  operationId: z.string().uuid(),
  createdAt: Timestamp,
  mode: z.enum(['full', 'delta']),
  diffDigest: z.string().length(64),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  lines: z.array(OnecPieceDiffLineSchema),
  linesTotal: z.number().int().nonnegative(),
  filter: OnecPieceDiffFilterSchema,
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  filterCounts: z.record(z.string(), z.number().int().nonnegative()),
  appliedCount: z.number().int().nonnegative(),
  applied: z.object({
    operationId: z.string().uuid(),
    state: OperationStateSchema,
    createdAt: Timestamp,
  }).strict().nullable(),
}).strict()

export const OnecPieceLatestDiffSchema = z.object({ diff: OnecPieceDiffSchema.nullable() }).strict()

export const OnecPieceApplyQueuedSchema = z.object({
  operationId: z.string().uuid(),
  title: z.string().min(1),
  state: OperationStateSchema,
}).strict()

export type OnecSyncRequest = z.infer<typeof OnecSyncRequestSchema>
export type OnecSyncRun = z.infer<typeof OnecSyncRunSchema>
export type OnecSyncerStatus = z.infer<typeof OnecSyncerStatusSchema>
export type OnecSyncRequestCreated = z.infer<typeof OnecSyncRequestCreatedSchema>
export type OnecSyncFailureReason = z.infer<typeof OnecSyncFailureReasonSchema>
export type OnecSyncSettings = z.infer<typeof OnecSyncSettingsSchema>
export type OnecPieceDiff = z.infer<typeof OnecPieceDiffSchema>
export type OnecPieceDiffLine = z.infer<typeof OnecPieceDiffLineSchema>
export type OnecPieceApplyQueued = z.infer<typeof OnecPieceApplyQueuedSchema>
export type OnecPieceDiffFilter = z.infer<typeof OnecPieceDiffFilterSchema>
